import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { baseApi, transformErrorResponse } from '@ibk/shared-config';
import { mapGetPixKeyDetailsV3ToPixKeyDetails } from '../../hooks/useMapGetPixKeyDetailsV3ToPixKeyDetails';
export const pixKeysApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        getPixKeys: builder.query({
            query: () => ({
                url: '/v1/pix/keys',
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
            providesTags: result => result?.result
                ? [
                    ...result.result.map(({ key }) => ({ type: 'PixKeys', id: key })),
                    { type: 'PixKeys', id: 'LIST' },
                ]
                : [{ type: 'PixKeys', id: 'LIST' }],
        }),
        getPixKeysV2: builder.query({
            query: () => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: '/v2/pix/keys',
                    forceSelectedAccountInfoAccountId: true,
                    forceAccountantApiBaseUrl: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformErrorResponse,
            providesTags: result => result?.result
                ? [
                    ...result.result.map(({ key }) => ({ type: 'PixKeys', id: key })),
                    { type: 'PixKeys', id: 'LIST' },
                ]
                : [{ type: 'PixKeys', id: 'LIST' }],
        }),
        getPixKey: builder.query({
            query: key => ({
                url: `/v2/pix/keys/${key}`,
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
        }),
        registerPixKey: builder.mutation({
            query: request => ({
                url: '/v1/pix/keys',
                method: 'POST',
                body: request,
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        validatePixKey: builder.mutation({
            query: ({ key, request }) => ({
                url: `/v1/pix/keys/${key}/validate`,
                method: 'POST',
                body: request,
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        resendPixKeyCode: builder.mutation({
            query: ({ key, request }) => ({
                url: `/v1/pix/keys/${key}/validate/resend-code`,
                method: 'POST',
                body: request,
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        deletePixKey: builder.mutation({
            query: key => ({
                url: `/v1/pix/keys/${key}`,
                method: 'DELETE',
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        deletePixKeyV2: builder.mutation({
            query: key => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: `/v2/pix/keys/${key}`,
                    method: 'DELETE',
                    forceSelectedAccountInfoAccountId: true,
                    forceAccountantApiBaseUrl: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        getPixKeyDetails: builder.query({
            query: ({ key }) => ({
                url: `/v2/pix/keys/${encodeURIComponent(key)}`,
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
        }),
        getPixKeyDetailsV3: builder.query({
            query: ({ key }) => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: `/v3/pix/keys/${encodeURIComponent(key)}`,
                    forceSelectedAccountInfoAccountId: true,
                    forceAccountantApiBaseUrl: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformResponse: (response) => {
                const raw = response && typeof response === 'object' && 'result' in response && response.result
                    ? response.result
                    : response;
                const mapped = mapGetPixKeyDetailsV3ToPixKeyDetails(raw);
                if (response && typeof response === 'object' && 'success' in response) {
                    return { ...response, result: mapped };
                }
                return { result: mapped, success: true };
            },
            transformErrorResponse,
        }),
        postValidatePixQrCodeKey: builder.mutation({
            query: ({ key }) => ({
                url: '/v2/pix/validate-pix-qrcode',
                method: 'POST',
                body: { content: key },
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
        }),
        postValidatePixQrCodeKeyV3: builder.mutation({
            query: ({ key }) => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: '/v3/pix/validate-pix-qrcode',
                    method: 'POST',
                    body: { content: key },
                    forceSelectedAccountInfoAccountId: true,
                    forceAccountantApiBaseUrl: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
        }),
        registerPixKeyV2: builder.mutation({
            query: request => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: '/v2/pix/keys',
                    method: 'POST',
                    body: request,
                    forceSelectedAccountInfoAccountId: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        validatePixKeyV2: builder.mutation({
            query: ({ key, request }) => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: `/v2/pix/keys/${key}/validate`,
                    method: 'POST',
                    body: request,
                    forceSelectedAccountInfoAccountId: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        resendPixKeyCodeV2: builder.mutation({
            query: ({ key, request }) => {
                const partyId = getSelectedAccountInfo()?.targetId;
                return {
                    url: `/v2/pix/keys/${key}/validate/resend-code`,
                    method: 'POST',
                    body: request,
                    forceSelectedAccountInfoAccountId: true,
                    ...(partyId && { headers: { 'x-tr-party-selected': partyId } }),
                };
            },
            transformErrorResponse,
            invalidatesTags: [{ type: 'PixKeys', id: 'LIST' }],
        }),
        validatePixKeysBatch: builder.mutation({
            query: ({ keys }) => ({
                url: '/v3/pix/keys/validate-batch',
                method: 'POST',
                body: { keys },
                forceSelectedAccountInfoAccountId: true,
                forceAccountantApiBaseUrl: true,
            }),
            transformErrorResponse,
        }),
    }),
});
export const { useLazyGetPixKeysQuery, useGetPixKeysQuery, useGetPixKeysV2Query, useLazyGetPixKeysV2Query, useLazyGetPixKeyQuery, useGetPixKeyQuery, useRegisterPixKeyMutation, useRegisterPixKeyV2Mutation, useValidatePixKeyMutation, useValidatePixKeyV2Mutation, useResendPixKeyCodeMutation, useResendPixKeyCodeV2Mutation, useDeletePixKeyMutation, useDeletePixKeyV2Mutation, useGetPixKeyDetailsQuery, useGetPixKeyDetailsV3Query, useLazyGetPixKeyDetailsQuery, useLazyGetPixKeyDetailsV3Query, usePostValidatePixQrCodeKeyMutation, usePostValidatePixQrCodeKeyV3Mutation, useValidatePixKeysBatchMutation, } = pixKeysApi;
